import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminAttachPhoneNumberBodySchema,
  type AdminAttachPhoneNumberBody,
  type AdminPhoneNumber,
} from '../../../src/shared/numbers';
import {
  adminKeys,
  attachTenantNumber,
  configureTenantNumberWebhooks,
  fetchAdminTenant,
  listTenantNumbers,
  releaseTenantNumber,
} from '../../api/admin';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';

/**
 * `/admin/tenants/:id/numbers` — tenant phone-number provisioning
 * (`src/shared/numbers.ts`). v1 is ATTACH, not purchase: the operator buys the
 * number in the Twilio console and records it here. A tenant with no active
 * number silently drops every inbound call and SMS (the webhook is marked
 * `ignored` and answered 200), so this page is what unblocks routing.
 */

const TWILIO_BUY_NUMBER_URL = 'https://console.twilio.com/us1/develop/phone-numbers/manage/search';

const inputClass =
  'mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none';
const labelClass = 'block text-sm font-medium text-slate-700';

export function TenantNumbersPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  if (id === undefined) {
    // Unreachable via the route table; fail loud rather than render blank.
    throw new Error('TenantNumbersPage rendered without a :id route param');
  }
  return <TenantNumbersPanel tenantId={id} />;
}

function TenantNumbersPanel({ tenantId }: { tenantId: string }): React.JSX.Element {
  const tenant = useQuery({
    queryKey: adminKeys.tenant(tenantId),
    queryFn: ({ signal }) => fetchAdminTenant(tenantId, signal),
  });
  const numbers = useQuery({
    queryKey: adminKeys.tenantNumbers(tenantId),
    queryFn: ({ signal }) => listTenantNumbers(tenantId, signal),
  });

  if (tenant.isPending || numbers.isPending) {
    return <LoadingScreen />;
  }
  if (tenant.isError) {
    return (
      <main className="mx-auto w-full max-w-2xl px-6 py-10">
        <ErrorNotice error={tenant.error} />
      </main>
    );
  }
  if (numbers.isError) {
    return (
      <main className="mx-auto w-full max-w-2xl px-6 py-10">
        <ErrorNotice error={numbers.error} />
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-6 py-10">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">
          Phone numbers — {tenant.data.tenant.name}
        </h1>
        <Link
          to="/admin"
          className="text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
        >
          Back to admin
        </Link>
      </div>
      <p className="mt-2 text-slate-600">
        Numbers attached here route inbound calls and SMS to this tenant. A tenant with no active
        number cannot receive anything.
      </p>

      <NumbersList tenantId={tenantId} numbers={numbers.data.numbers} />
      <AttachNumberForm tenantId={tenantId} />
    </main>
  );
}

function NumbersList({
  tenantId,
  numbers,
}: {
  tenantId: string;
  numbers: AdminPhoneNumber[];
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const configureMutation = useMutation({
    mutationFn: (numberId: string) => configureTenantNumberWebhooks(tenantId, numberId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.tenantNumbers(tenantId) });
    },
  });
  const releaseMutation = useMutation({
    mutationFn: (numberId: string) => releaseTenantNumber(tenantId, numberId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.tenantNumbers(tenantId) });
    },
  });

  return (
    <section className="mt-6">
      <h2 className="text-lg font-medium">Attached numbers</h2>
      {numbers.length === 0 ? (
        <div
          className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
          data-testid="numbers-empty"
        >
          No numbers attached yet — this tenant cannot receive calls or SMS.
        </div>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="numbers-list">
          {numbers.map((number) => {
            const released = number.status === 'released';
            return (
              <li
                key={number.id}
                className={`rounded-lg border p-4 shadow-sm ${
                  released
                    ? 'border-slate-200 bg-slate-100 opacity-60'
                    : 'border-slate-200 bg-white'
                }`}
                data-testid={`number-row-${number.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-slate-900">
                        {number.e164}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          released ? 'bg-slate-200 text-slate-600' : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {number.status}
                      </span>
                      {number.capabilities.voice && (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                          voice
                        </span>
                      )}
                      {number.capabilities.sms && (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                          sms
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      Webhook setup:{' '}
                      {number.webhookStatus === 'configured'
                        ? 'Configured'
                        : number.webhookStatus === 'failed'
                          ? 'Failed — retry required'
                          : 'Not verified'}
                    </p>
                    {number.webhookError !== null && (
                      <p className="mt-1 text-sm text-red-700">{number.webhookError}</p>
                    )}
                    <p className="mt-1 text-xs text-slate-500">
                      <code className="font-mono">{number.providerSid}</code> · updated{' '}
                      {new Date(number.updatedAt).toLocaleString()}
                    </p>
                  </div>
                  {!released && (
                    <button
                      type="button"
                      onClick={() => configureMutation.mutate(number.id)}
                      disabled={configureMutation.isPending || releaseMutation.isPending}
                      className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                      data-testid={`configure-btn-${number.id}`}
                    >
                      {configureMutation.isPending && configureMutation.variables === number.id
                        ? 'Configuring…'
                        : 'Configure webhooks'}
                    </button>
                  )}
                  {!released && (
                    <button
                      type="button"
                      onClick={() => releaseMutation.mutate(number.id)}
                      disabled={releaseMutation.isPending || configureMutation.isPending}
                      className="shrink-0 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid={`release-btn-${number.id}`}
                    >
                      {releaseMutation.isPending && releaseMutation.variables === number.id
                        ? 'Releasing…'
                        : 'Release'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {releaseMutation.isError && <ErrorNotice error={releaseMutation.error} className="mt-3" />}
      {configureMutation.isError && (
        <ErrorNotice error={configureMutation.error} className="mt-3" />
      )}
    </section>
  );
}

function AttachNumberForm({ tenantId }: { tenantId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [e164, setE164] = useState('');
  const [providerSid, setProviderSid] = useState('');
  const [voice, setVoice] = useState(true);
  const [sms, setSms] = useState(true);
  const [configureWebhooks, setConfigureWebhooks] = useState(true);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const attachMutation = useMutation({
    mutationFn: (body: AdminAttachPhoneNumberBody) => attachTenantNumber(tenantId, body),
    onSettled: () => {
      setE164('');
      setProviderSid('');
      void queryClient.invalidateQueries({ queryKey: adminKeys.tenantNumbers(tenantId) });
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = AdminAttachPhoneNumberBodySchema.safeParse({
      e164,
      providerSid,
      capabilities: { voice, sms },
      configureWebhooks,
    });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Check the form and try again.');
      return;
    }
    setFieldError(null);
    attachMutation.mutate(parsed.data);
  };

  return (
    <section className="mt-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-medium">Attach a number</h2>
        <a
          href={TWILIO_BUY_NUMBER_URL}
          target="_blank"
          rel="noreferrer"
          className="whitespace-nowrap text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
        >
          Buy a number in the Twilio console ↗
        </a>
      </div>
      <p className="mt-1 text-sm text-slate-600">
        Record a number you already own in Twilio. This does not purchase anything.
      </p>

      <form className="mt-4 space-y-4" onSubmit={handleSubmit} noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="number-e164" className={labelClass}>
              Phone number (E.164)
            </label>
            <input
              id="number-e164"
              type="tel"
              autoComplete="off"
              required
              placeholder="+15551234567"
              value={e164}
              onChange={(event) => setE164(event.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="number-sid" className={labelClass}>
              Twilio SID
            </label>
            <input
              id="number-sid"
              type="text"
              autoComplete="off"
              required
              placeholder="PN…"
              value={providerSid}
              onChange={(event) => setProviderSid(event.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={voice}
              onChange={(event) => setVoice(event.target.checked)}
              className="rounded border-slate-300"
            />
            Voice
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={sms}
              onChange={(event) => setSms(event.target.checked)}
              className="rounded border-slate-300"
            />
            SMS
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={configureWebhooks}
            onChange={(event) => setConfigureWebhooks(event.target.checked)}
            className="rounded border-slate-300"
          />
          Configure webhooks in Twilio (points Voice/SMS URLs at this deployment)
        </label>

        {fieldError !== null && <p className="text-sm text-red-600">{fieldError}</p>}
        {attachMutation.isError && <ErrorNotice error={attachMutation.error} />}

        <button
          type="submit"
          disabled={attachMutation.isPending}
          className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          {attachMutation.isPending ? 'Attaching…' : 'Attach number'}
        </button>
      </form>
    </section>
  );
}
